import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import api from "@/utils/axios";
import cls from "./MemberProfileEditPaymentsNew.module.scss";

const MemberProfileEditPaymentsNew = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(); // common/en.json

  const [loadingOptions, setLoadingOptions] = useState(true);
  const [optionsError, setOptionsError] = useState("");
  const [options, setOptions] = useState([]);

  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [transferDate, setTransferDate] = useState("");

  const [proofFile, setProofFile] = useState(null);
  const [proofPreviewUrl, setProofPreviewUrl] = useState("");
  const [proofUploading, setProofUploading] = useState(false);
  const [proofError, setProofError] = useState("");
  const [proofImageKey, setProofImageKey] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  /* -------------------------
   * Load active payment methods
   * ------------------------- */
  useEffect(() => {
    let isMounted = true;

    const fetchOptions = async () => {
      try {
        setLoadingOptions(true);
        setOptionsError("");

        const res = await api.get("/v1/member-create/payment/options");
        if (!isMounted) return;

        const list = res.data?.paymentOptions || [];
        setOptions(list);
        if (!list.length) {
          setOptionsError(t("memberProfilePaymentsNew.errors.noActiveOptions"));
        }
      } catch (err) {
        if (!isMounted) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          t("memberProfilePaymentsNew.errors.genericLoadError");
        setOptionsError(msg);
      } finally {
        if (isMounted) setLoadingOptions(false);
      }
    };

    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, [t]);

  const handleBack = () => {
    navigate("/lounge/my-profile/edit/payments");
  };

  /* -------------------------
   * Proof file selection
   * ------------------------- */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProofFile(file);
    setProofError("");
    setProofImageKey("");
    if (proofPreviewUrl) {
      URL.revokeObjectURL(proofPreviewUrl);
    }
    setProofPreviewUrl(URL.createObjectURL(file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    setProofFile(file);
    setProofError("");
    setProofImageKey("");
    if (proofPreviewUrl) {
      URL.revokeObjectURL(proofPreviewUrl);
    }
    setProofPreviewUrl(URL.createObjectURL(file));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /* -------------------------
   * Upload proof via presign
   * ------------------------- */
  const uploadProof = useCallback(async () => {
    if (!proofFile) {
      setProofError(t("memberProfilePaymentsNew.upload.errors.noFileSelected"));
      return;
    }

    try {
      setProofUploading(true);
      setProofError("");
      setSubmitError("");

      // Derive extension & content type
      const nameParts = proofFile.name?.split(".") || [];
      let ext = (nameParts[nameParts.length - 1] || "jpg").toLowerCase();
      if (ext === "jpeg") ext = "jpg";
      const contentType = proofFile.type || "application/octet-stream";

      // 1) Get presigned URL + processed base key
      const presignRes = await api.post(
        "/v1/member-create/payment/proof/presign",
        {
          ext,
          contentType,
        },
      );

      const { uploadUrl, processedBaseKey } = presignRes.data || {};
      if (!uploadUrl || !processedBaseKey) {
        throw new Error(
          t("memberProfilePaymentsNew.upload.errors.missingPresignData"),
        );
      }

      // 2) Upload original file directly to S3
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
        },
        body: proofFile,
      });

      if (!putRes.ok) {
        throw new Error(
          t("memberProfilePaymentsNew.upload.errors.uploadFailed"),
        );
      }

      // 3) Assume processed image will be available at /poster.webp
      const processedKey = `${processedBaseKey}/images/poster.webp`;

      setProofImageKey(processedKey);
      setProofError("");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t("memberProfilePaymentsNew.upload.errors.genericUploadError");
      setProofError(msg);
      setProofImageKey("");
    } finally {
      setProofUploading(false);
    }
  }, [proofFile, t]);

  /* -------------------------
   * Submit final payment
   * ------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    if (!selectedOptionId) {
      setSubmitError(
        t("memberProfilePaymentsNew.errors.validation.noPaymentMethod"),
      );
      return;
    }
    if (!transferDate) {
      setSubmitError(
        t("memberProfilePaymentsNew.errors.validation.noTransferDate"),
      );
      return;
    }
    if (!proofImageKey) {
      setSubmitError(
        t("memberProfilePaymentsNew.errors.validation.noProofUploaded"),
      );
      return;
    }

    try {
      setSubmitting(true);

      const res = await api.post("/v1/member-create/payment/submit-approved", {
        paymentOptionId: selectedOptionId,
        transferDate,
        proofImageKey,
      });

      const nextPath = res.data?.next;
      const message =
        res.data?.message ||
        t("memberProfilePaymentsNew.states.successDefaultMessage");

      setSubmitSuccess(message);

      // If backend provides a next path, go there
      if (nextPath && typeof nextPath === "string") {
        navigate(nextPath);
      } else {
        // fallback: go back to list
        navigate("/lounge/my-profile/edit/payments");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t("memberProfilePaymentsNew.errors.genericSubmitError");
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const hasOptions = !loadingOptions && !optionsError && options.length > 0;
  const isSubmitDisabled =
    submitting || proofUploading || !selectedOptionId || !transferDate;

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          <span className={cls.backIcon} aria-hidden="true">
            ←
          </span>
          <span>{t("memberProfilePaymentsNew.actions.backToPayments")}</span>
        </button>

        <div className={cls.headerText}>
          <h1 className={cls.title}>{t("memberProfilePaymentsNew.title")}</h1>
          <p className={cls.subtitle}>
            {t("memberProfilePaymentsNew.subtitle")}
          </p>
        </div>
      </header>

      <section className={cls.stepRow}>
        <div className={cls.step}>
          <span className={cls.stepIndex}>1</span>
          <span className={cls.stepLabel}>
            {t("memberProfilePaymentsNew.steps.method")}
          </span>
        </div>
        <div className={cls.step}>
          <span className={cls.stepIndex}>2</span>
          <span className={cls.stepLabel}>
            {t("memberProfilePaymentsNew.steps.details")}
          </span>
        </div>
        <div className={cls.step}>
          <span className={cls.stepIndex}>3</span>
          <span className={cls.stepLabel}>
            {t("memberProfilePaymentsNew.steps.proof")}
          </span>
        </div>
      </section>

      {loadingOptions && (
        <div className={cls.stateBox}>
          <div className={cls.loader} />
          <span>{t("memberProfilePaymentsNew.states.loadingOptions")}</span>
        </div>
      )}

      {!loadingOptions && optionsError && (
        <div className={cls.stateBoxError}>
          <span className={cls.stateLabel}>
            {t("memberProfilePaymentsNew.states.errorLabel")}
          </span>
          <p className={cls.stateMessage}>{optionsError}</p>
        </div>
      )}

      <form className={cls.form} onSubmit={handleSubmit}>
        <div className={cls.mainColumn}>
          {/* Payment method selection */}
          <section className={cls.cardSection}>
            <h2 className={cls.sectionTitle}>
              {t("memberProfilePaymentsNew.sections.methodTitle")}
            </h2>
            <p className={cls.sectionText}>
              {t("memberProfilePaymentsNew.sections.methodText")}
            </p>

            {hasOptions && (
              <Motion.div
                className={cls.optionsGrid}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                {options.map((opt, index) => (
                  <Motion.button
                    key={opt._id || opt.id || opt.name}
                    type="button"
                    className={`${cls.optionCard} ${
                      selectedOptionId === (opt._id || opt.id)
                        ? cls.optionCardActive
                        : ""
                    }`}
                    onClick={() => setSelectedOptionId(opt._id || opt.id || "")}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.04 * index, duration: 0.25 }}
                  >
                    <div className={cls.optionHeader}>
                      <span className={cls.optionBadge}>
                        {t(
                          "memberProfilePaymentsNew.fields.paymentOption.badge",
                        )}
                      </span>
                      <h3 className={cls.optionTitle}>
                        {opt.title || opt.name}
                      </h3>
                    </div>
                    {opt.description && (
                      <p className={cls.optionDescription}>{opt.description}</p>
                    )}
                    {opt.instruction && (
                      <p className={cls.optionInstruction}>{opt.instruction}</p>
                    )}
                  </Motion.button>
                ))}
              </Motion.div>
            )}

            {hasOptions && (
              <p className={cls.helperNote}>
                {t("memberProfilePaymentsNew.sections.methodHelperNote")}
              </p>
            )}
          </section>

          {/* Transfer details */}
          <section className={cls.cardSection}>
            <h2 className={cls.sectionTitle}>
              {t("memberProfilePaymentsNew.sections.detailsTitle")}
            </h2>

            <div className={cls.fieldRow}>
              <label className={cls.fieldLabel}>
                {t("memberProfilePaymentsNew.fields.transferDate.label")}
              </label>
              <input
                type="date"
                className={cls.input}
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
              />
              <p className={cls.fieldHelp}>
                {t("memberProfilePaymentsNew.fields.transferDate.help")}
              </p>
            </div>
          </section>

          {/* Proof upload */}
          <section className={cls.cardSection}>
            <h2 className={cls.sectionTitle}>
              {t("memberProfilePaymentsNew.sections.proofTitle")}
            </h2>
            <p className={cls.sectionText}>
              {t("memberProfilePaymentsNew.sections.proofText")}
            </p>

            <div
              className={cls.uploadBox}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className={cls.uploadInner}>
                <p className={cls.uploadTitle}>
                  {t("memberProfilePaymentsNew.upload.title")}
                </p>
                <p className={cls.uploadText}>
                  {t("memberProfilePaymentsNew.upload.text")}
                </p>

                <div className={cls.uploadActions}>
                  <label className={cls.uploadBtn}>
                    <span>
                      {t("memberProfilePaymentsNew.upload.pickButton")}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className={cls.uploadInput}
                    />
                  </label>

                  <button
                    type="button"
                    className={cls.uploadSecondaryBtn}
                    onClick={uploadProof}
                    disabled={!proofFile || proofUploading}
                  >
                    {proofUploading
                      ? t("memberProfilePaymentsNew.upload.uploadingLabel")
                      : t("memberProfilePaymentsNew.upload.uploadButton")}
                  </button>
                </div>

                <p className={cls.uploadHint}>
                  {t("memberProfilePaymentsNew.upload.hint")}
                </p>

                {proofError && <p className={cls.uploadError}>{proofError}</p>}

                {proofImageKey && !proofError && (
                  <p className={cls.uploadSuccess}>
                    {t("memberProfilePaymentsNew.upload.success")}
                  </p>
                )}
              </div>

              {proofPreviewUrl && (
                <div className={cls.previewBox}>
                  <img
                    src={proofPreviewUrl}
                    alt={t("memberProfilePaymentsNew.upload.previewAlt")}
                    className={cls.previewImage}
                  />
                </div>
              )}
            </div>
          </section>

          {submitError && (
            <div className={cls.stateBoxError}>
              <span className={cls.stateLabel}>
                {t("memberProfilePaymentsNew.states.errorLabel")}
              </span>
              <p className={cls.stateMessage}>{submitError}</p>
            </div>
          )}

          {submitSuccess && (
            <div className={cls.stateBoxSuccess}>
              <span className={cls.stateLabel}>
                {t("memberProfilePaymentsNew.states.successLabel")}
              </span>
              <p className={cls.stateMessage}>{submitSuccess}</p>
            </div>
          )}

          <div className={cls.actionsRow}>
            <button
              type="submit"
              className={cls.submitBtn}
              disabled={isSubmitDisabled}
            >
              {submitting
                ? t("memberProfilePaymentsNew.actions.submitting")
                : t("memberProfilePaymentsNew.actions.submit")}
            </button>

            <button
              type="button"
              className={cls.cancelBtn}
              onClick={handleBack}
            >
              {t("memberProfilePaymentsNew.actions.cancel")}
            </button>
          </div>
        </div>

        {/* Right column: friendly summary */}
        <aside className={cls.sideColumn}>
          <div className={cls.summaryCard}>
            <h2 className={cls.summaryTitle}>
              {t("memberProfilePaymentsNew.summary.title")}
            </h2>
            <p className={cls.summaryText}>
              {t("memberProfilePaymentsNew.summary.text")}
            </p>

            <ul className={cls.summaryList}>
              <li>
                <span className={cls.summaryBullet}>✓</span>
                <span>
                  {t("memberProfilePaymentsNew.summary.items.secure")}
                </span>
              </li>
              <li>
                <span className={cls.summaryBullet}>✓</span>
                <span>
                  {t("memberProfilePaymentsNew.summary.items.reviewed")}
                </span>
              </li>
              <li>
                <span className={cls.summaryBullet}>✓</span>
                <span>
                  {t("memberProfilePaymentsNew.summary.items.update")}
                </span>
              </li>
            </ul>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default MemberProfileEditPaymentsNew;
